'use client';

import { type ElementType, forwardRef, useContext, useId } from 'react';

import { css, cx } from '@/styled-system/css';

import { useIsomorphicLayoutEffect } from '../../hooks/use-isomorphic-layout-effect';
import { MenuGroupContext } from './context/menu-group.context';
import { MenuRootContext } from './context/menu-root.context';
import { menuHeadingStyles } from './styles/menu-heading.styles';
import type { MenuHeadingPropsInterface } from './types/menu-heading.types';

/**
 * @since 10.0.0
 */
export const MenuHeading = forwardRef<HTMLHeadingElement, MenuHeadingPropsInterface>(
  (props: MenuHeadingPropsInterface, forwardedRef) => {
    const { className, css: cssProp, level, ...rest } = props;

    const labelId = useId();
    const { menu } = useContext(MenuRootContext);
    const { id } = useContext(MenuGroupContext);

    const styles = css(menuHeadingStyles, cssProp);

    useIsomorphicLayoutEffect(() => {
      if (!id) {
        menu.setLabelId(labelId);
      }
      return () => menu.setLabelId(undefined);
    }, [id, labelId, menu.setLabelId]);

    const Component: ElementType = `h${level}`;

    return (
      <Component
        ref={forwardedRef}
        data-c11n-component="Menu.Heading"
        data-c11n-version={__C11N_VERSION__}
        className={cx('c11n', styles, className)}
        id={id || menu.labelId}
        {...rest}
      />
    );
  },
);

MenuHeading.displayName = 'Menu.Heading';
